import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DashboardStats from './components/DashboardStats';
import RecentOrders from './components/RecentOrders';
import WishlistSection from './components/WishlistSection';
import AccountSettings from './components/AccountSettings';
import AddressBook from './components/AddressBook';
import PaymentMethods from './components/PaymentMethods';
import SupportSection from './components/SupportSection';

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Mock user data
  const userProfile = {
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@email.com",
    phone: "+1 (555) 123-4567",
    dateOfBirth: "1990-05-15",
    memberSince: "2022-03-15",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
  };

  const dashboardStats = {
    totalOrders: 24,
    totalSpent: 3456.78,
    wishlistItems: 12,
    loyaltyPoints: 2340
  };

  const recentOrders = [
    {
      id: "ORD-2023-001",
      orderDate: "2023-09-20T10:30:00Z",
      status: "Delivered",
      total: 899.99,
      items: [
        {
          name: "iPhone 15 Pro Max",
          image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=400&fit=crop"
        },
        {
          name: "MagSafe Charger",
          image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop"
        }
      ]
    },
    {
      id: "ORD-2023-002",
      orderDate: "2023-09-18T14:15:00Z",
      status: "Shipped",
      total: 1299.99,
      items: [
        {
          name: "MacBook Air M2",
          image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=400&h=400&fit=crop"
        }
      ]
    },
    {
      id: "ORD-2023-003",
      orderDate: "2023-09-15T09:45:00Z",
      status: "Processing",
      total: 199.99,
      items: [
        {
          name: "AirPods Pro 2nd Gen",
          image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400&h=400&fit=crop"
        }
      ]
    }
  ];

  const wishlistItems = [
    {
      id: 1,
      name: "Samsung Galaxy S24 Ultra",
      brand: "Samsung",
      price: 1199.99,
      originalPrice: 1299.99,
      image: "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=400&h=400&fit=crop",
      category: "phones",
      inStock: true,
      addedDate: "2023-09-10T12:00:00Z"
    },
    {
      id: 2,
      name: "Dell XPS 13 Plus",
      brand: "Dell",
      price: 1499.99,
      image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=400&fit=crop",
      category: "laptops",
      inStock: true,
      addedDate: "2023-09-08T16:30:00Z"
    },
    {
      id: 3,
      name: "Sony WH-1000XM5",
      brand: "Sony",
      price: 399.99,
      originalPrice: 449.99,
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop",
      category: "accessories",
      inStock: false,
      addedDate: "2023-09-05T11:15:00Z"
    }
  ];

  const addresses = [
    {
      id: 1,
      type: "home",
      firstName: "John",
      lastName: "Doe",
      company: "",
      address1: "123 Main Street",
      address2: "Apt 4B",
      city: "New York",
      state: "NY",
      zipCode: "10001",
      country: "United States",
      phone: "+1 (555) 123-4567",
      isDefault: true
    },
    {
      id: 2,
      type: "work",
      firstName: "John",
      lastName: "Doe",
      company: "Tech Corp Inc.",
      address1: "456 Business Ave",
      address2: "Suite 200",
      city: "New York",
      state: "NY",
      zipCode: "10002",
      country: "United States",
      phone: "+1 (555) 987-6543",
      isDefault: false
    }
  ];

  const paymentMethods = [
    {
      id: 1,
      type: "credit",
      brand: "Visa",
      cardNumber: "4532123456789012",
      expiryMonth: "12",
      expiryYear: "2027",
      cardholderName: "John Doe",
      isDefault: true
    },
    {
      id: 2,
      type: "credit",
      brand: "Mastercard",
      cardNumber: "5555444433332222",
      expiryMonth: "08",
      expiryYear: "2026",
      cardholderName: "John Doe",
      isDefault: false
    },
    {
      id: 3,
      type: "paypal",
      email: "john.doe@email.com",
      isDefault: false
    }
  ];

  const supportTickets = [
    {
      id: "TKT-2023-001",
      subject: "Order delivery issue",
      category: "order",
      priority: "high",
      status: "in-progress",
      description: "My order was supposed to be delivered yesterday but I haven\'t received it yet.",
      createdAt: "2023-09-20T10:00:00Z",
      lastUpdated: "2023-09-21T14:30:00Z",
      lastResponse: "We\'ve contacted the shipping carrier and your package should arrive by end of day today."
    },
    {
      id: "TKT-2023-002",
      subject: "Product warranty question",
      category: "product",
      priority: "medium",
      status: "resolved",
      description: "I need information about the warranty coverage for my recent laptop purchase.",
      createdAt: "2023-09-18T09:15:00Z",
      lastUpdated: "2023-09-19T11:45:00Z",
      lastResponse: "Your laptop comes with a 2-year manufacturer warranty. Documentation has been emailed to you."
    }
  ];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'LayoutDashboard' },
    { id: 'orders', name: 'Orders', icon: 'Package' },
    { id: 'wishlist', name: 'Wishlist', icon: 'Heart' },
    { id: 'account', name: 'Account', icon: 'User' },
    { id: 'addresses', name: 'Addresses', icon: 'MapPin' },
    { id: 'payments', name: 'Payments', icon: 'CreditCard' },
    { id: 'support', name: 'Support', icon: 'HelpCircle' }
  ];

  // Event handlers
  const handleViewOrder = (orderId) => {
    console.log('View order:', orderId);
  };

  const handleReorder = (orderId) => {
    console.log('Reorder:', orderId);
  };

  const handleTrackOrder = (orderId) => {
    console.log('Track order:', orderId);
  };

  const handleRemoveFromWishlist = (itemId) => {
    console.log('Remove from wishlist:', itemId);
  };

  const handleMoveToCart = (itemId) => {
    console.log('Move to cart:', itemId);
  };

  const handleUpdateProfile = (profileData) => {
    console.log('Update profile:', profileData);
  };

  const handleChangePassword = (passwordData) => {
    console.log('Change password:', passwordData);
  };

  const handleAddAddress = (addressData) => {
    console.log('Add address:', addressData);
  };

  const handleEditAddress = (addressId, addressData) => {
    console.log('Edit address:', addressId, addressData);
  };

  const handleDeleteAddress = (addressId) => {
    console.log('Delete address:', addressId);
  };

  const handleSetDefaultAddress = (addressId) => {
    console.log('Set default address:', addressId);
  };

  const handleAddPaymentMethod = (paymentData) => {
    console.log('Add payment method:', paymentData);
  };

  const handleDeletePaymentMethod = (paymentId) => {
    console.log('Delete payment method:', paymentId);
  };

  const handleSetDefaultPayment = (paymentId) => {
    console.log('Set default payment:', paymentId);
  };

  const handleCreateTicket = (ticketData) => {
    console.log('Create ticket:', ticketData);
  };

  const handleUpdateTicket = (ticketId, updateData) => {
    console.log('Update ticket:', ticketId, updateData);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <DashboardStats stats={dashboardStats} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <RecentOrders
                orders={recentOrders}
                onViewOrder={handleViewOrder}
                onReorder={handleReorder}
                onTrackOrder={handleTrackOrder}
              />
              <div className="holographic-card p-6 rounded-lg">
                <h2 className="text-xl font-bold command-text mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Package"
                    iconPosition="left"
                    onClick={() => setActiveTab('orders')}
                  >
                    View All Orders
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Heart"
                    iconPosition="left"
                    onClick={() => setActiveTab('wishlist')}
                  >
                    Manage Wishlist
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Settings"
                    iconPosition="left"
                    onClick={() => setActiveTab('account')}
                  >
                    Account Settings
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="HelpCircle"
                    iconPosition="left"
                    onClick={() => setActiveTab('support')}
                  >
                    Get Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'orders':
        return (
          <RecentOrders
            orders={recentOrders}
            onViewOrder={handleViewOrder}
            onReorder={handleReorder}
            onTrackOrder={handleTrackOrder}
          />
        );
      case 'wishlist':
        return (
          <WishlistSection
            wishlistItems={wishlistItems}
            onRemoveFromWishlist={handleRemoveFromWishlist}
            onMoveToCart={handleMoveToCart}
            onAddToCart={handleMoveToCart}
          />
        );
      case 'account':
        return (
          <AccountSettings
            userProfile={userProfile}
            onUpdateProfile={handleUpdateProfile}
            onChangePassword={handleChangePassword}
          />
        );
      case 'addresses':
        return (
          <AddressBook
            addresses={addresses}
            onAddAddress={handleAddAddress}
            onEditAddress={handleEditAddress}
            onDeleteAddress={handleDeleteAddress}
            onSetDefault={handleSetDefaultAddress}
          />
        );
      case 'payments':
        return (
          <PaymentMethods
            paymentMethods={paymentMethods}
            onAddPaymentMethod={handleAddPaymentMethod}
            onDeletePaymentMethod={handleDeletePaymentMethod}
            onSetDefaultPayment={handleSetDefaultPayment}
          />
        );
      case 'support':
        return (
          <SupportSection
            supportTickets={supportTickets}
            onCreateTicket={handleCreateTicket}
            onUpdateTicket={handleUpdateTicket}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* User Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
                <img
                  src={userProfile?.avatar}
                  alt={`${userProfile?.firstName} ${userProfile?.lastName}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold command-text">
                  Welcome back, {userProfile?.firstName}!
                </h1>
                <p className="text-muted-foreground">
                  Member since {new Date(userProfile.memberSince)?.toLocaleDateString()}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto scrollbar-hide">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>
      {/* Data Stream Animation */}
      <div className="data-stream"></div>
    </div>
  );
};

export default UserDashboard;